/**
 * UI-6c Task 5 — QuestionEditor.
 *
 * A controlled list-of-things editor for the questions on a `CsatConfig`. The
 * parent (`CsatConfigDrawer`) owns the array — this component only renders
 * rows and reports user edits back via `onChange(next)`.
 *
 * Behaviour:
 *   - Each row exposes a type Dropdown ('Score (1-5)' | 'Yes/No'), a prompt
 *     InputText, and a Remove icon button.
 *   - "+ Add question" appends `{ id: crypto.randomUUID(), type: 'score',
 *     prompt: '', weight: 1 }`. The stable id lets React reconcile rows
 *     across renders without keying on array index (which would re-mount
 *     siblings when an intermediate row is removed).
 *   - The LAST remaining question cannot be removed — the survey must always
 *     have ≥1 question (matches backend `questions.min(1)`). The Remove
 *     button is disabled when `value.length <= 1`, and the click handler is
 *     also a no-op in that case as belt + suspenders.
 *
 * The editor is intentionally schema-agnostic — it doesn't know about Zod or
 * react-hook-form. The drawer wraps it in a `Controller` so it participates
 * in form validation.
 */
import type { JSX } from 'react'
import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * Shape of a single CSAT question. Matches the server-side
 * `QuestionSchema` in `apps/api/src/routes/admin/csat/create.ts`:
 *   - `id` is optional on the wire (server fills it in), but the editor
 *     always carries a stable id locally so rows have a React key.
 *   - `type` is one of the two Phase-1 question types.
 *   - `prompt` is the customer-facing string (1-500 chars, validated by the
 *     parent's Zod schema — not enforced inline here).
 *   - `weight` defaults to 1 when adding a fresh row.
 */
export interface CsatQuestion {
  id?: string
  type: 'score' | 'yes_no'
  prompt: string
  weight?: number
}

export interface QuestionEditorProps {
  value: CsatQuestion[]
  onChange: (next: CsatQuestion[]) => void
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TYPE_OPTIONS: Array<{ label: string; value: CsatQuestion['type'] }> = [
  { label: 'Score (1-5)', value: 'score' },
  { label: 'Yes/No', value: 'yes_no' },
]

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function QuestionEditor(props: QuestionEditorProps): JSX.Element {
  const { value, onChange } = props

  function addQuestion(): void {
    onChange([
      ...value,
      {
        id: crypto.randomUUID(),
        type: 'score',
        prompt: '',
        weight: 1,
      },
    ])
  }

  function updateQuestion(idx: number, patch: Partial<CsatQuestion>): void {
    onChange(value.map((q, i) => (i === idx ? { ...q, ...patch } : q)))
  }

  function removeQuestion(idx: number): void {
    // Survey must always carry ≥1 question — match backend `min(1)` constraint.
    // The Remove button is also `disabled` so users rarely reach this path,
    // but the runtime guard is the source of truth.
    if (value.length <= 1) return
    onChange(value.filter((_, i) => i !== idx))
  }

  return (
    <div className="space-y-2" data-testid="question-editor">
      {value.map((q, idx) => {
        // Fall back to the row index when `id` is absent (defensive — every
        // freshly added question has a UUID, but server-loaded ones MIGHT
        // arrive without one if the API is ever extended).
        const rowKey = q.id ?? `row-${idx}`
        return (
          <div key={rowKey} className="flex items-start gap-2">
            <Dropdown
              value={q.type}
              options={TYPE_OPTIONS}
              onChange={(e) =>
                updateQuestion(idx, { type: e.value as CsatQuestion['type'] })
              }
              aria-label={`Question ${idx + 1} type`}
            />
            <InputText
              value={q.prompt}
              onChange={(e) =>
                updateQuestion(idx, { prompt: e.target.value })
              }
              placeholder="Enter prompt shown to customer"
              className="flex-1"
              aria-label={`Question ${idx + 1} prompt`}
            />
            <Button
              icon="pi pi-trash"
              severity="danger"
              text
              disabled={value.length <= 1}
              onClick={() => removeQuestion(idx)}
              aria-label={`Remove question ${idx + 1}`}
            />
          </div>
        )
      })}
      <Button
        label="+ Add question"
        outlined
        onClick={addQuestion}
        aria-label="Add question"
      />
    </div>
  )
}
